import React, { useState } from 'react';
import { CircularHealthRing } from './CircularHealthRing';
import { UserPrescribedRules } from './UserPrescribedRules';
import { HealthDimension } from '../../types/report';
import { useTranslate } from '../../hooks/useTranslate';



interface DataQualityInfographProps {
    healthScore: number;
    healthDimensions: HealthDimension[];
    className?: string;
    bare?: boolean; // when true, omit internal card chrome so parent can style container
    minimalChart?: boolean; // when true (usually with bare), render only the chart core
    userRulesData?: any; // User-prescribed rules data from backend - if null/undefined, section won't show
}

export function DataQualityInfograph({
    healthScore,
    healthDimensions,
    className = '',
    bare = false,
    minimalChart = false,
    userRulesData
}: DataQualityInfographProps) {
    // Score clamping retained implicitly inside CircularHealthRing; keep local for aria label continuity
    const rawScore = Number.isFinite(healthScore) ? healthScore : 0;
    const clampedScore = Math.min(100, Math.max(0, rawScore));
    const { t } = useTranslate();

    // Bigger donut size to match background
    const size = minimalChart ? 320 : 280; // Much bigger
    const baseRadius = minimalChart ? 120 : 110; // Bigger to match background

    const [isVisible, setIsVisible] = useState(false);

    // Trigger fade-in animation on mount
    React.useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, []);

    // Unified card container (no longer conditional)
    return (
        <div
            className={`
                data-quality-card 
                bg-gradient-to-br from-white to-red-50
                rounded-2xl 
                shadow-sm
                p-4 lg:p-8
                max-w-7xl
                mx-auto
                transition-opacity duration-500 ease-out
                ${isVisible ? 'opacity-100' : 'opacity-0'}
                ${className}
            `}
        >
            {/* Section 1: Data Quality Overview */}
            <section
                className={`
                    overview mb-8 
                    transition-opacity duration-500 ease-out delay-100
                    ${isVisible ? 'opacity-100' : 'opacity-0'}
                `}
            >
                {!minimalChart && (
                    <div className="mb-8 text-center">
                        <h3
                            className="text-4xl lg:text-5xl font-bold text-black mb-4"
                            style={{ fontFamily: 'Alright Sans, system-ui, sans-serif' }}
                        >
                            {t('Data Quality Overview')}
                        </h3>
                        <p className="text-xl text-gray-500">
                            {t('Comprehensive quality assessment across key dimensions')}
                        </p>
                    </div>
                )}

                <div className={minimalChart ? 'flex justify-center' : 'grid grid-cols-2 gap-8 lg:gap-12 items-center'}>
                    {/* Compact Donut Chart - Always Left */}
                    <div className={minimalChart ? 'relative flex items-center justify-center min-h-[280px]' : 'relative flex items-center justify-center min-h-[240px]'}>
                        <CircularHealthRing
                            score={clampedScore}
                            size={size}
                            baseRadius={baseRadius}
                            prominent={minimalChart}
                            ariaLabel={`Overall data health score ${clampedScore}${healthDimensions.length ? ` across ${healthDimensions.length} dimensions` : ''}`}
                            className="max-w-full"
                        />
                        {minimalChart && (
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                                <div className="w-[360px] h-[360px] rounded-full bg-gradient-to-tr from-cyan-400/5 via-purple-500/5 to-transparent blur-3xl" />
                            </div>
                        )}
                    </div>

                    {/* Quality Dimensions - Always Right */}
                    {!minimalChart && (
                        <div className="space-y-6">
                            <h4
                                className="text-2xl font-semibold text-left"
                                style={{ color: '#A4001D' }}
                            >
                                {t('Quality Dimensions')}
                            </h4>
                            <QualityDimensionsList dimensions={healthDimensions} />
                        </div>
                    )}
                </div>
            </section>

            {/* Section 2: User-Prescribed Rules - Show with mock data for development */}
            {!minimalChart && (
                <div
                    className={`
                        transition-opacity duration-500 ease-out delay-200
                        ${isVisible ? 'opacity-100' : 'opacity-0'}
                    `}
                >
                    <UserPrescribedRules dqFrameworkResults={userRulesData} />
                </div>
            )}
        </div>
    );
}

/** Strip emoji / symbol / pictograph characters. */
function stripEmojis(str: string): string {
    return str.replace(/[\u{1F600}-\u{1F9FF}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1FA00}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}\u{E0020}-\u{E007F}]/gu, '').replace(/  +/g, ' ');
}

// Helper to parse summary text into bullet points
function parseSummaryToBullets(text: string): string[] {
    if (!text) return [];
    const cleaned = stripEmojis(text);
    // Try JSON first
    try {
        const parsed = JSON.parse(cleaned);
        if (Array.isArray(parsed)) return parsed.map(String).filter(s => s.length > 0);
        if (typeof parsed === 'object') return Object.entries(parsed).map(([k, v]) => `${k}: ${v}`);
    } catch { /* not JSON, continue */ }

    // Split on real newlines first, then expand inline numbered items, then sentence boundaries
    const lines = cleaned
        .split('\n')
        .flatMap(line => {
            const trimmed = line.trim();
            if (!trimmed) return [];
            // Split inline numbered items: "text 1) item 2) item" → separate entries
            const parenHits = trimmed.match(/\d{1,3}\)\s+/g);
            if (parenHits && parenHits.length >= 2) {
                return trimmed.split(/\s+(?=\d{1,3}\)\s+)/)
                    .map(p => p.trim())
                    .filter(p => p.length > 0)
                    .map(p => p.replace(/^(?:\d+[.)]\s+|[-•*]\s+)/, '').trim())
                    .filter(p => p.length > 5);
            }
            // Strip leading bullets/numbers for uniform display
            const stripped = trimmed.replace(/^(?:\d+[.)]\s+|[-•*]\s+)/, '').trim();
            // Try splitting on sentence boundaries within a single long line
            if (stripped.length > 120) {
                return stripped.split(/(?<=[.!?])\s+(?=[A-Z])/).map(s => s.trim()).filter(s => s.length > 5);
            }
            return stripped ? [stripped] : [];
        })
        .filter(s => s.length > 5);
    return lines.length > 0 ? lines : [text.trim()];
}

// Refined QualityDimensionsList – clean, professional, on-brand
interface QualityDimensionsListProps {
    dimensions: HealthDimension[];
}

const QualityDimensionsList: React.FC<QualityDimensionsListProps> = ({ dimensions }) => {
    const [expandedDimension, setExpandedDimension] = useState<string | null>(null);
    const { t } = useTranslate();

    const toggleExpanded = (dimensionKey: string) => {
        setExpandedDimension(expandedDimension === dimensionKey ? null : dimensionKey);
    };

    return (
        <div className="space-y-3" aria-labelledby="quality-dimensions-heading">
            <ul className="space-y-2" role="list">
                {dimensions.map((dimension) => {
                    const rawScore = typeof dimension.score === 'string'
                        ? parseFloat((dimension.score as string).replace('%', ''))
                        : typeof dimension.score === 'number'
                            ? dimension.score
                            : 0;
                    const percent = Math.min(100, Math.max(0, rawScore || 0));
                    const isExpanded = expandedDimension === dimension.key;
                    const hasDetails = dimension.reason || dimension.evidence;

                    return (
                        <li key={dimension.key}>
                            {/* Dimension Row */}
                            <button
                                type="button"
                                className={`w-full text-left rounded-xl px-4 py-3 transition-all duration-200 border ${isExpanded
                                    ? 'bg-white border-[#D31145]/20 shadow-md'
                                    : hasDetails
                                        ? 'bg-white/60 border-transparent hover:bg-white hover:border-gray-200 hover:shadow-sm cursor-pointer'
                                        : 'bg-white/60 border-transparent cursor-default'
                                    }`}
                                onClick={() => hasDetails && toggleExpanded(dimension.key)}
                                aria-expanded={isExpanded}
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <span
                                        className="text-gray-800 text-sm font-semibold flex-shrink-0"
                                        aria-label={`${dimension.label} score ${percent} percent`}
                                    >
                                        {t(dimension.label)}
                                    </span>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <div
                                            className="w-28 h-2 bg-gray-100 rounded-full overflow-hidden"
                                            role="progressbar"
                                            aria-valuenow={percent}
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                            aria-label={`${dimension.label} progress`}
                                        >
                                            <div
                                                className="h-full rounded-full transition-all duration-1000 ease-out"
                                                style={{
                                                    width: `${percent}%`,
                                                    background: 'linear-gradient(to right, #A4001D, #D31145)'
                                                }}
                                            />
                                        </div>
                                        <span className="notranslate text-gray-900 text-sm w-12 text-right font-bold tabular-nums" translate="no">
                                            {percent}%
                                        </span>
                                        {hasDetails && (
                                            <svg
                                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                                fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </button>

                            {/* Expanded Detail Panel */}
                            {isExpanded && (
                                <div className="mt-1 ml-2 mr-2 rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden animate-in slide-in-from-top-1 duration-200">
                                    <div className="p-5 space-y-4">
                                        {/* Score badge */}
                                        <div className="flex items-center gap-3">
                                            <span className={`notranslate inline-flex items-center px-3 py-1 rounded-full text-xs font-bold tracking-wide ${percent >= 90 ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' :
                                                percent >= 75 ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' :
                                                    percent >= 50 ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-200' :
                                                        'bg-red-50 text-red-700 ring-1 ring-red-200'
                                                }`} translate="no">
                                                {typeof dimension.score === 'string' ? dimension.score : `${percent}%`}
                                            </span>
                                            <div className="flex-1 max-w-[200px]">
                                                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full transition-all duration-700 ease-out"
                                                        style={{
                                                            width: `${percent}%`,
                                                            background: 'linear-gradient(to right, #A4001D, #D31145)'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Reason / Overview */}
                                        {dimension.reason && (
                                            <div>
                                                <h5 className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color: '#D31145' }}>{t('Overview')}</h5>
                                                <p className="notranslate text-sm text-gray-700 leading-relaxed" translate="no">{stripEmojis(dimension.reason)}</p>
                                            </div>
                                        )}

                                        {/* Evidence Sections */}
                                        {dimension.evidence && (
                                            <div className="space-y-3">
                                                {typeof dimension.evidence === 'object' && !Array.isArray(dimension.evidence) ? (
                                                    <>
                                                        {/* Summary */}
                                                        {(dimension.evidence.Summary || dimension.evidence.summary) && (
                                                            <EvidenceSection title={t('Summary')}>
                                                                <BulletList items={parseSummaryToBullets(dimension.evidence.Summary || dimension.evidence.summary)} />
                                                            </EvidenceSection>
                                                        )}

                                                        {/* Technical Details */}
                                                        {dimension.evidence.Technical_Details && (
                                                            <EvidenceSection title={t('Technical Details')}>
                                                                {dimension.evidence.Technical_Details.key_factors?.length > 0 && (
                                                                    <div className="mb-3">
                                                                        <h6 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5">{t('Key Factors')}</h6>
                                                                        <BulletList items={dimension.evidence.Technical_Details.key_factors} />
                                                                    </div>
                                                                )}
                                                                {dimension.evidence.Technical_Details.methodology && (
                                                                    <div className="mb-3">
                                                                        <h6 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5">{t('Methodology')}</h6>
                                                                        {dimension.evidence.Technical_Details.methodology.formula && (
                                                                            <code className="notranslate block text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2 mb-2 font-mono" translate="no">
                                                                                {dimension.evidence.Technical_Details.methodology.formula}
                                                                            </code>
                                                                        )}
                                                                        {dimension.evidence.Technical_Details.methodology.score_breakdown && (
                                                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                                                                {Object.entries(dimension.evidence.Technical_Details.methodology.score_breakdown).map(([key, value]) => (
                                                                                    <div key={key} className="flex justify-between text-xs py-1 border-b border-gray-50">
                                                                                        <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                                                                                        <span className="text-gray-800 font-medium font-mono">{typeof value === 'number' ? value.toFixed(2) : String(value)}</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                {dimension.evidence.Technical_Details.dq_validation && (
                                                                    <div>
                                                                        <h6 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5">{t('Validation')}</h6>
                                                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                                                            {Object.entries(dimension.evidence.Technical_Details.dq_validation).map(([key, value]) => (
                                                                                <div key={key} className="notranslate flex justify-between text-xs py-1 border-b border-gray-50" translate="no">
                                                                                    <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                                                                                    <span className="text-gray-800 font-medium font-mono">{String(value)}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </EvidenceSection>
                                                        )}

                                                        {/* Column Breakdown */}
                                                        {dimension.evidence["Column breakdown"] && (
                                                            <EvidenceSection title={t('Column Breakdown')}>
                                                                <BulletList items={parseSummaryToBullets(dimension.evidence["Column breakdown"])} />
                                                            </EvidenceSection>
                                                        )}

                                                        {/* Methodology */}
                                                        {dimension.evidence["Overall methodology"] && (
                                                            <EvidenceSection title={t('Methodology')}>
                                                                <BulletList items={parseSummaryToBullets(dimension.evidence["Overall methodology"])} />
                                                            </EvidenceSection>
                                                        )}
                                                    </>
                                                ) : Array.isArray(dimension.evidence) ? (
                                                    <EvidenceSection title={t('Evidence')}>
                                                        <div className="notranslate space-y-2" translate="no">
                                                            {(dimension.evidence as any[]).map((evidence: any, idx: number) => (
                                                                <div key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                                                                    <span className="text-[#D31145] mt-0.5 flex-shrink-0">&#8226;</span>
                                                                    <div>
                                                                        {evidence.table && <span className="font-medium text-gray-900">{evidence.table}</span>}
                                                                        {evidence.column && <span className="text-gray-500"> / {evidence.column}</span>}
                                                                        {(evidence.table || evidence.column) && evidence.details && <span className="text-gray-400"> — </span>}
                                                                        {evidence.details && <span>{evidence.details}</span>}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </EvidenceSection>
                                                ) : (
                                                    <EvidenceSection title={t('Evidence')}>
                                                        <p className="notranslate text-sm text-gray-700 leading-relaxed" translate="no">{String(dimension.evidence)}</p>
                                                    </EvidenceSection>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

// Clean section wrapper for evidence blocks
function EvidenceSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="bg-gray-50/70 rounded-lg p-4 border border-gray-100">
            <h5 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: '#D31145' }}>{title}</h5>
            {children}
        </div>
    );
}

// Consistent bullet list rendering with header detection
function BulletList({ items }: { items: string[] }) {
    return (
        <ul className="notranslate space-y-1.5" translate="no">
            {items.map((item, idx) => {
                const cleaned = stripEmojis(item);

                // Detect standalone header ending with colon (no body text)
                // e.g. "Immediate Actions:" → render as bold header without bullet
                const standaloneMatch = cleaned.match(/^([A-Z][A-Za-z0-9 &/,'()-]{2,58}):\s*$/);
                if (standaloneMatch) {
                    return (
                        <li key={idx} className="text-sm font-bold text-gray-900 leading-relaxed list-none mt-2 first:mt-0">
                            {standaloneMatch[1]}
                        </li>
                    );
                }

                // Detect "Header: body" pattern (1-6 uppercase-starting words before colon)
                const headerMatch = cleaned.match(/^([A-Z][A-Za-z0-9 &/,'()-]{1,58}):\s+(.+)/);
                if (headerMatch) {
                    const headerWords = headerMatch[1].trim().split(/\s+/);
                    if (headerWords.length >= 1 && headerWords.length <= 6) {
                        return (
                            <li key={idx} className="text-sm text-gray-700 leading-relaxed flex items-start gap-2">
                                <span className="text-[#D31145] mt-0.5 flex-shrink-0">&#8226;</span>
                                <span>
                                    <span className="font-bold text-gray-900">{headerMatch[1]}:</span>{' '}
                                    <span>{headerMatch[2]}</span>
                                </span>
                            </li>
                        );
                    }
                }
                return (
                    <li key={idx} className="text-sm text-gray-700 leading-relaxed flex items-start gap-2">
                        <span className="text-[#D31145] mt-0.5 flex-shrink-0">&#8226;</span>
                        <span>{cleaned}</span>
                    </li>
                );
            })}
        </ul>
    );
}
